import { z } from 'zod';
import { paginationSchema } from './api.js';
import { LEAD_SOURCES } from './enums.js';
/**
 * The CRM object model — GBV-DEV-2026-001 Section 09.
 *
 *   Company      the business
 *   Person       an individual, usually at a Company
 *   Opportunity  a deal against a Company and a Person
 *
 * Lead is unchanged and sits in front of all three: it is the unqualified
 * enquiry, and it *converts* into them.
 */
/** Indian mobile numbers: permissive in, strictly E.164 out. */
const phoneInput = z
    .string()
    .trim()
    .min(10, 'A phone number needs at least 10 digits.')
    .max(20, 'That phone number is too long.')
    .regex(/^[+0-9][0-9\s\-()]*$/, 'Use digits, spaces, or dashes only.');
const optionalEmail = z
    .string()
    .trim()
    .toLowerCase()
    .email('Enter a valid email.')
    .optional()
    .or(z.literal(''));
// ── Company ──────────────────────────────────────────────────
export const COMPANY_SIZES = ['SOLO', 'MICRO', 'SMALL', 'MEDIUM', 'LARGE'];
/** Headcount bands as an Indian SME would describe itself. */
export const COMPANY_SIZE_LABELS = {
    SOLO: 'Just the owner',
    MICRO: '2–10 people',
    SMALL: '11–50 people',
    MEDIUM: '51–200 people',
    LARGE: '200+ people',
};
export const companyCreateSchema = z.object({
    name: z.string().trim().min(2, 'Enter the company name.').max(160),
    legalName: z.string().trim().max(200).optional().or(z.literal('')),
    /**
     * Stored as a bare host. A full URL is accepted and reduced, because people
     * paste from the address bar and "https://x.in/" and "x.in" are one company.
     */
    domain: z.string().trim().max(200).optional().or(z.literal('')),
    industry: z.string().trim().max(80).optional().or(z.literal('')),
    size: z.enum(COMPANY_SIZES).optional().nullable(),
    gstin: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/, 'That is not a valid 15-character GSTIN.')
        .optional()
        .or(z.literal('')),
    phone: phoneInput.optional().or(z.literal('')),
    email: optionalEmail,
    addressLine: z.string().trim().max(240).optional().or(z.literal('')),
    city: z.string().trim().max(80).optional().or(z.literal('')),
    state: z.string().trim().max(80).optional().or(z.literal('')),
    pincode: z.string().trim().regex(/^[1-9][0-9]{5}$/, 'A PIN code is six digits.').optional().or(z.literal('')),
    notes: z.string().trim().max(2000).optional().or(z.literal('')),
    ownerId: z.string().cuid().optional().nullable(),
});
export const companyUpdateSchema = companyCreateSchema.partial();
export const companyListQuery = paginationSchema.extend({
    search: z.string().trim().max(120).optional(),
    ownerId: z.string().cuid().optional(),
    industry: z.string().trim().max(80).optional(),
});
// ── Person ───────────────────────────────────────────────────
export const personCreateSchema = z.object({
    name: z.string().trim().min(2, 'Enter the person’s name.').max(120),
    phone: phoneInput,
    altPhone: phoneInput.optional().or(z.literal('')),
    email: optionalEmail,
    designation: z.string().trim().max(120).optional().or(z.literal('')),
    companyId: z.string().cuid().optional().nullable(),
    city: z.string().trim().max(80).optional().or(z.literal('')),
    notes: z.string().trim().max(2000).optional().or(z.literal('')),
    ownerId: z.string().cuid().optional().nullable(),
});
export const personUpdateSchema = personCreateSchema.partial();
export const personListQuery = paginationSchema.extend({
    search: z.string().trim().max(120).optional(),
    companyId: z.string().cuid().optional(),
    ownerId: z.string().cuid().optional(),
});
// ── Opportunity ──────────────────────────────────────────────
export const OPPORTUNITY_STAGES = [
    'QUALIFICATION',
    'NEEDS_ANALYSIS',
    'PROPOSAL',
    'NEGOTIATION',
    'WON',
    'LOST',
];
export const OPPORTUNITY_STAGE_LABELS = {
    QUALIFICATION: 'Qualification',
    NEEDS_ANALYSIS: 'Needs analysis',
    PROPOSAL: 'Proposal',
    NEGOTIATION: 'Negotiation',
    WON: 'Won',
    LOST: 'Lost',
};
/** The two stages that end a deal. Neither belongs on the working board. */
export const CLOSED_STAGES = ['WON', 'LOST'];
export const OPEN_STAGES = OPPORTUNITY_STAGES.filter((s) => !CLOSED_STAGES.includes(s));
/**
 * Starting odds per stage. A default, not a rule — a rep's read on a specific
 * deal beats a table, so probability stays editable.
 */
export const STAGE_PROBABILITY = {
    QUALIFICATION: 10,
    NEEDS_ANALYSIS: 25,
    PROPOSAL: 50,
    NEGOTIATION: 75,
    WON: 100,
    LOST: 0,
};
export const opportunityCreateSchema = z.object({
    name: z.string().trim().min(3, 'Name the deal so it is recognisable in a list.').max(160),
    companyId: z.string().cuid().optional().nullable(),
    primaryPersonId: z.string().cuid().optional().nullable(),
    valuePaise: z
        .number()
        .int('Amounts are in whole paise.')
        .min(0, 'A deal cannot be worth less than nothing.')
        .max(100_00_00_000, 'That is larger than this system accepts.')
        .default(0),
    stage: z.enum(OPPORTUNITY_STAGES).default('QUALIFICATION'),
    probability: z.number().int().min(0).max(100).optional(),
    expectedCloseAt: z.coerce.date().optional().nullable(),
    source: z.enum(LEAD_SOURCES).default('OTHER'),
    ownerId: z.string().cuid().optional().nullable(),
});
export const opportunityUpdateSchema = opportunityCreateSchema
    .partial()
    .extend({ lostReason: z.string().trim().max(400).optional() })
    .refine((v) => v.stage !== 'LOST' || Boolean(v.lostReason && v.lostReason.length > 0), {
    path: ['lostReason'],
    message: 'Say why it was lost — it is the most useful field we collect.',
});
/** Moving a card on the board. Separate from a full edit for the same reason. */
export const opportunityStageSchema = z
    .object({
    stage: z.enum(OPPORTUNITY_STAGES),
    lostReason: z.string().trim().max(400).optional(),
})
    .refine((v) => v.stage !== 'LOST' || Boolean(v.lostReason && v.lostReason.length > 0), {
    path: ['lostReason'],
    message: 'Say why it was lost — it is the most useful field we collect.',
});
export const opportunityListQuery = paginationSchema.extend({
    search: z.string().trim().max(120).optional(),
    stage: z.enum(OPPORTUNITY_STAGES).optional(),
    companyId: z.string().cuid().optional(),
    personId: z.string().cuid().optional(),
    ownerId: z.string().cuid().optional(),
    /** Open deals only — the default view, since closed ones are history. */
    openOnly: z.coerce.boolean().optional(),
});
// ── Lead conversion ──────────────────────────────────────────
export const leadConvertSchema = z.object({
    /**
     * Attach to an existing company, or let the server find-or-create one from
     * the name. Sending both is fine; the id wins.
     */
    companyId: z.string().cuid().optional().nullable(),
    companyName: z.string().trim().max(160).optional().or(z.literal('')),
    /** Off when the lead is worth recording as a contact but has no deal yet. */
    createOpportunity: z.boolean().default(true),
    opportunityName: z.string().trim().max(160).optional().or(z.literal('')),
    valuePaise: z.number().int().min(0).max(100_00_00_000).optional(),
    expectedCloseAt: z.coerce.date().optional().nullable(),
});
//# sourceMappingURL=crm.schema.js.map